import React, { useContext, useEffect, useRef, useState } from 'react'
import NextLink from 'next/link'
import { Link as AscendLink } from '@achieve/ascend'
import { useRouter } from 'next/router'
import { TrackingWrapper } from 'components/Tracking/TrackingWrapper'
import { ROUTE_EXCEPT_NOFOLLOW, ROUTE_ASSET_EXCEPT } from 'constants/navigation'
import { getProductAndAddDefaultUtmsToLink } from 'utils/queryManipulation'
import Cookies from 'js-cookie'
import { disableClientSideRoutes } from 'constants/experiments'
import { useFeatureFlag } from 'hooks/useFeatureFlag'
import { FEATURE_TOGGLES } from 'constants/featureToggles'
import { preserveQueryParams, replaceUrlMpLXExperiment, sanitizeUrlParams } from 'utils/shared'
import { LayoutContext } from 'providers/LayoutProvider'

const LinkWrapper = React.forwardRef(({ children, refAs, ...props }, ref) => {
  if (refAs) {
    props[refAs] = ref
  }

  return (
    <>
      {React.isValidElement(children)
        ? React.cloneElement(children, props)
        : typeof children === 'string'
        ? children
        : null}
    </>
  )
})

LinkWrapper.displayName = 'LinkWrapper'

/**
 *
 * @param {string} href original href string
 * @param {ReactNode} children child components wrapped with AchieveLink
 * @param {boolean} withNextLink if true use next/link, default use AscendLink
 * @param {RefObject} refAs external ref to be used in the link child
 * @returns {ReactElement} returns link wrapped child component with forwarded
 *                         ref, passed props and transformed/memoized href
 *                         with original query params preserved
 */
const moveAnchorRight = (url) => {
  if (!url) {
    return url
  }
  let anchorIndex = url.indexOf('#')
  let queryIndex = url.indexOf('?')
  if (queryIndex > anchorIndex && queryIndex > -1 && anchorIndex > -1) {
    let dividedUrl = url.split('#')
    let anchorAndQuery = dividedUrl[1].split('?')
    return `${dividedUrl[0]}?${anchorAndQuery[1]}#${anchorAndQuery[0]}`
  }
  return url
}

const AchieveLink = ({
  href,
  children,
  withNextLink = false,
  refAs,
  track,
  onClick,
  rel,
  noLink,
  noPreserveParams,
  ...restProps
}) => {
  let route = useRouter()
  const trackRefApiResponse = useRef()
  const { state: { siteEnv } = {} } = useContext(LayoutContext)

  const enableMpLXExperiment = useFeatureFlag(FEATURE_TOGGLES[siteEnv]?.ACX_WEB_DR_MP_VS_LX)

  const [finalHref, setFinalHref] = useState(href)
  const [trackAsConversion, setTrackAsConversion] = useState(false)
  if (!route.asPath) {
    route = { ...route, query: {}, asPath: '/' }
  }
  let { query = {} } = route || {}

  if (Object.keys(query).length === 0 && route.asPath.includes('?')) {
    const urlParams = new URLSearchParams(route.asPath.split('?')[1])
    for (const [key, value] of urlParams.entries()) {
      if (value instanceof Array) {
        query = { ...query, [key]: value[value.length - 1] }
      } else {
        query = { ...query, [key]: value }
      }
    }
  } else {
    for (const key of Object.keys(query)) {
      const value = query[key]
      if (value instanceof Array) {
        query[key] = value[value.length - 1]
      } else {
        query[key] = value
      }
    }
  }

  const visitId = Cookies.get('eh_visit_id')
  useEffect(() => {
    let calcHref = href
    calcHref = getProductAndAddDefaultUtmsToLink(preserveQueryParams(href, query, visitId))
    if (noPreserveParams) calcHref = href
    calcHref = moveAnchorRight(calcHref)
    setFinalHref(sanitizeUrlParams(calcHref))
  }, [href, noPreserveParams, query, visitId])

  // ACX_WEB_DR_MP_VS_LX experiment
  useEffect(() => {
    if (enableMpLXExperiment && enableMpLXExperiment?.toLowerCase() === 'variation_a') {
      setTrackAsConversion(true)
      let newUrl = replaceUrlMpLXExperiment(finalHref, route.pathname)
      setFinalHref(newUrl)
    }
  }, [route, enableMpLXExperiment, finalHref])

  const noFollowExceptionRoute = ROUTE_EXCEPT_NOFOLLOW.findIndex(
    (exception) => String(`${href}`).toLowerCase().includes(exception) >= 0
  )
  if (
    String(`${href}`).toLowerCase().includes('http') &&
    noFollowExceptionRoute == -1 &&
    !String(`${href}`).toLowerCase().includes(ROUTE_ASSET_EXCEPT)
  ) {
    if (!String(`${linkRel}`).toLowerCase().includes('nofollow')) {
      linkRel = `${rel ? `${rel} ` : ''}nofollow`
    }
    restProps = { ...restProps, target: '_blank' }
  }
  if (disableClientSideRoutes.includes(finalHref)) {
    onClick = (e) => {
      e?.preventDefault()
      window.location.href = finalHref
    }
  }
  let linkRel = rel ?? null
  const LinkComponent = withNextLink && !noLink ? NextLink : AscendLink
  const nextProps = withNextLink && !noLink ? { passHref: true } : {}

  const handleClick = () => {
    if (track) {
      trackRefApiResponse.current.click()
    }
    if (onClick) {
      onClick()
    }
  }

  return (
    <>
      <LinkComponent
        component={finalHref && !noLink ? 'a' : 'span'}
        {...(noLink ? {} : { href: finalHref })}
        rel={linkRel}
        underline="hover"
        onClick={handleClick}
        {...restProps}
        {...nextProps}
      >
        {children?.type === 'a' ? children : <LinkWrapper refAs={refAs}>{children}</LinkWrapper>}
      </LinkComponent>
      <TrackingWrapper
        track={{ href: finalHref, ...track }}
        onClick={onClick}
        withNextLink={withNextLink}
        trackAsConversion={trackAsConversion}
        className="!hidden"
      >
        <div ref={trackRefApiResponse}></div>
      </TrackingWrapper>
    </>
  )
}

export { AchieveLink }
